import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import LeaderboardSettings from './LeaderboardSettings';
import {
  Card, Header, TitleGroup, Title, PeriodLabel, GearButton,
  ScopeBar, ScopeSelect,
  Table, HeadRow, Th, BodyRow, PosCell, NameCell, NameInner, Avatar, NameText, YouTag, PointsCell,
  Empty, Footer, OptInButton, EnablePrompt, EnableButton,
} from './LeaderboardWidget.styles';

const MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };

function getInitials(name) {
  return (name || '').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// Hard-mode green awards 1.5x, so totals can be fractional (e.g. 7.5). Show one decimal only when needed.
function fmtPoints(p) {
  return Number.isInteger(p) ? String(p) : p.toFixed(1);
}

function formatPeriodLabel(cadence, periodStartIso) {
  switch (cadence) {
    case 'weekly': return 'This week';
    case 'monthly': return 'This month';
    case 'quarterly': return 'This quarter';
    case 'custom':
      if (periodStartIso) {
        const d = new Date(periodStartIso);
        // Period boundaries are UTC midnights — render in UTC or US users see the prior day.
        return `Since ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
      }
      return 'Current period';
    default: return '';
  }
}

// Competition ranking (1, 2, 2, 4) over an already points-desc-sorted list.
function rankWithinScope(list) {
  let rank = 0;
  let prev = null;
  return list.map((e, idx) => {
    if (prev === null || e.points < prev) { rank = idx + 1; prev = e.points; }
    return { userId: e.userId, displayName: e.displayName, points: e.points, isCurrentUser: e.isCurrentUser, rank };
  });
}

const LeaderboardWidget = observer(({ companyStore, usersStore, leaderboardStore }) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [optInSaving, setOptInSaving] = useState(false);
  const [selectedScope, setSelectedScope] = useState('company');
  const [selectedSeason, setSelectedSeason] = useState('current');

  const company = companyStore?.company;
  const companyId = company?.id;
  const currentUser = usersStore?.currentUser;
  // Company Admins AND Multiply super-admins can enable / configure the leaderboard.
  const canManage = !!(currentUser?.isCompanyAdmin || currentUser?.isAdmin);
  // Executives can also browse past seasons (read-only history), but never configure the board.
  const canViewSeasons = canManage || !!currentUser?.isExecutive;

  // Lazy load on mount and whenever the company's leaderboard config changes (e.g. an
  // admin just enabled it, changed cadence, or edited groups). Gates on role, never isReadOnly.
  useEffect(() => {
    if (companyId) {
      leaderboardStore.load(companyId).catch(err => console.error('Failed to load leaderboard:', err));
    }
  }, [
    companyId,
    company?.leaderboardEnabled,
    company?.leaderboardResetCadence,
    company?.leaderboardCustomStart,
    company?.leaderboardCustomLengthDays,
    company?.leaderboardScoringMode,
    leaderboardStore,
  ]);

  // Groups-only saves change no primitive dep above (the MST array keeps its node
  // identity), so the settings modal reports back explicitly after a successful save.
  const handleSettingsSaved = () => {
    if (companyId) {
      leaderboardStore.load(companyId).catch(err => console.error('Failed to reload leaderboard:', err));
    }
  };

  // Season history: lazily load it once for Company Admins, Executives and super-admins.
  useEffect(() => {
    if (companyId && canViewSeasons) {
      leaderboardStore.loadSeasons(companyId).catch(err => console.error('Failed to load seasons:', err));
    }
  }, [companyId, canViewSeasons, leaderboardStore]);

  const enabled = leaderboardStore.loaded ? leaderboardStore.enabled : !!company?.leaderboardEnabled;

  // Active dataset: the live board, or a frozen past season the admin selected.
  const seasons = leaderboardStore.seasons;
  const viewingSeason = selectedSeason !== 'current' ? seasons.find(s => s.periodStart === selectedSeason) : null;
  const groups = viewingSeason ? viewingSeason.groups : leaderboardStore.groups;
  const allEntries = (viewingSeason ? viewingSeason.entries : leaderboardStore.entries).map(e => ({
    userId: e.userId, displayName: e.displayName, points: e.points, rank: e.rank,
    // Live entries carry isCurrentUser from the server; for a frozen season, highlight by id.
    isCurrentUser: e.isCurrentUser || (currentUser ? e.userId === currentUser.userId : false),
  }));
  const periodLabelText = viewingSeason
    ? (viewingSeason.label || 'Past season')
    : formatPeriodLabel(leaderboardStore.cadence, leaderboardStore.periodStart);

  // Fall back to the company view if a previously-selected group disappeared.
  const currentScope = (selectedScope === 'company' || groups.some(g => g.id === selectedScope))
    ? selectedScope
    : 'company';
  const selectedGroup = currentScope !== 'company' ? groups.find(g => g.id === currentScope) : null;

  let displayedEntries;
  if (selectedGroup) {
    const memberSet = new Set(selectedGroup.memberIds);
    displayedEntries = rankWithinScope(allEntries.filter(e => memberSet.has(e.userId)));
  } else {
    displayedEntries = allEntries;
  }

  const handleOptInToggle = async () => {
    if (!currentUser) return;
    setOptInSaving(true);
    try {
      // Targeted write of only leaderboardOptIn — never re-sends the full profile.
      await currentUser.setLeaderboardOptIn(!leaderboardStore.currentUserOptIn);
      await leaderboardStore.load(companyId);
    } catch (err) {
      console.error('Failed to update leaderboard opt-in:', err);
    } finally {
      setOptInSaving(false);
    }
  };

  // Disabled: only managers/super-admins see the enable CTA; everyone else gets nothing.
  if (!enabled) {
    if (!canManage) return null;
    return (
      <Card>
        <Header>
          <TitleGroup><Title>Leaderboard</Title></TitleGroup>
          <GearButton onClick={() => setSettingsOpen(true)} title="Leaderboard settings">{'⚙'}</GearButton>
        </Header>
        <EnablePrompt>
          <p>The company leaderboard is off. Turn it on to motivate your team with friendly, points-based competition.</p>
          <EnableButton onClick={() => setSettingsOpen(true)}>Enable Leaderboard</EnableButton>
        </EnablePrompt>
        <LeaderboardSettings companyStore={companyStore} usersStore={usersStore} open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={handleSettingsSaved} />
      </Card>
    );
  }

  return (
    <Card>
      <Header>
        <TitleGroup>
          <Title>Leaderboard</Title>
          <PeriodLabel>{periodLabelText}</PeriodLabel>
        </TitleGroup>
        {canManage && (
          <GearButton onClick={() => setSettingsOpen(true)} title="Leaderboard settings">{'⚙'}</GearButton>
        )}
      </Header>

      {((canViewSeasons && seasons.length > 0) || groups.length > 0) && (
        <ScopeBar>
          {canViewSeasons && seasons.length > 0 && (
            <ScopeSelect
              value={selectedSeason}
              onChange={e => { setSelectedSeason(e.target.value); setSelectedScope('company'); }}
              title="View a past season's final standings"
            >
              <option value="current">This season</option>
              {seasons.map(s => <option key={s.periodStart} value={s.periodStart}>{s.label || s.periodStart}</option>)}
            </ScopeSelect>
          )}
          {groups.length > 0 && (
            <ScopeSelect value={currentScope} onChange={e => setSelectedScope(e.target.value)}>
              <option value="company">Entire company</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </ScopeSelect>
          )}
        </ScopeBar>
      )}

      {displayedEntries.length === 0 ? (
        <Empty>{selectedGroup ? 'No one in this group yet.' : 'No members on the board yet.'}</Empty>
      ) : (
        <Table>
          <thead>
            <HeadRow>
              <Th $align="center">Position</Th>
              <Th>Name</Th>
              <Th $align="right" $divider>Points</Th>
            </HeadRow>
          </thead>
          <tbody>
            {displayedEntries.map((entry, idx) => {
              // A medal (and its podium tint) only makes sense once you've actually
              // scored — otherwise everyone at 0 would get gold/silver/bronze.
              const medalRank = entry.points > 0 && entry.rank <= 3 ? entry.rank : 0;
              return (
                <BodyRow key={entry.userId} $rank={medalRank} $isCurrentUser={entry.isCurrentUser} $zebra={idx % 2 === 1}>
                  <PosCell $rank={medalRank}>{medalRank ? MEDALS[medalRank] : entry.rank}</PosCell>
                  <NameCell>
                    <NameInner>
                      <Avatar>{getInitials(entry.displayName)}</Avatar>
                      <NameText $isCurrentUser={entry.isCurrentUser}>{entry.displayName}</NameText>
                      {entry.isCurrentUser && <YouTag>You</YouTag>}
                    </NameInner>
                  </NameCell>
                  <PointsCell>{fmtPoints(entry.points)}</PointsCell>
                </BodyRow>
              );
            })}
          </tbody>
        </Table>
      )}

      {!viewingSeason && leaderboardStore.currentUserEligibleToOptIn && (
        <Footer>
          <OptInButton onClick={handleOptInToggle} disabled={optInSaving}>
            {leaderboardStore.currentUserOptIn ? 'Hide me from the leaderboard' : 'Show me on the leaderboard'}
          </OptInButton>
        </Footer>
      )}

      {canManage && (
        <LeaderboardSettings companyStore={companyStore} usersStore={usersStore} open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={handleSettingsSaved} />
      )}
    </Card>
  );
});

export default LeaderboardWidget;
